// ==============================
// Imports
// ==============================

// MUI Components
import { createTheme, responsiveFontSizes, Theme } from '@mui/material/styles';

export type ColorMode = 'light' | 'dark';

// ==============================
// Theme factory
// ==============================
/**
 * Builds the app theme for a given color mode. MUI components pick up their
 * light/dark surfaces from `palette.mode`; the Dialog overrides below are
 * written as theme-aware functions so dialogs follow the surface color instead
 * of being pinned to white (which broke them in dark mode).
 */
export const getTheme = (mode: ColorMode): Theme => {
  const isDark = mode === 'dark';

  const baseTheme = createTheme({
    palette: {
      mode,
      primary: {
        main: '#4da7db',
      },
      secondary: {
        // Deep navy on light; lightened on dark so emphasis text stays legible.
        main: isDark ? '#9ec5e8' : '#102138',
      },
      success: {
        main: isDark ? '#66bb6a' : '#2e7d32',
      },
      error: {
        main: isDark ? '#f26d6d' : '#d32f2f',
      },
      warning: {
        main: isDark ? '#ffa726' : '#ed6c02',
      },
      info: {
        main: isDark ? '#4fc3f7' : '#0288d1',
      },
      ...(isDark
        ? {
            background: {
              default: '#0e1621',
              paper: '#16202c',
            },
          }
        : {}),
    },
    components: {
      MuiDialog: {
        styleOverrides: {
          paper: ({ theme }) => ({
            borderRadius: '4px !important',
            boxShadow: '0 11px 28px rgba(0, 0, 0, 0.22) !important',
            backgroundColor: `${theme.palette.background.paper} !important`,
            // MUI adds a translucent white "elevation overlay" as a
            // background-image on Paper in dark mode. The title/actions reset
            // `background` (clearing it) but the content doesn't, so the body
            // rendered a muddy gray while the header stayed dark. Drop the
            // overlay so the whole dialog surface is one flat color.
            backgroundImage: 'none !important',
            overflow: 'hidden',
          }),
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: ({ theme }) => ({
            padding: '16px 24px 8px !important',
            background: `${theme.palette.background.paper} !important`,
            color: `${theme.palette.text.primary} !important`,
            fontSize: '1.25rem !important',
            fontWeight: '400 !important',
            lineHeight: 1.5,
            '& .MuiTypography-root': {
              color: `${theme.palette.text.primary} !important`,
            },
            '& .MuiSvgIcon-root': {
              color: `${theme.palette.text.primary} !important`,
            },
            '& .MuiBox-root': {
              background: 'transparent !important',
              backdropFilter: 'none !important',
              boxShadow: 'none !important',
            },
          }),
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: ({ theme }) => ({
            padding: '8px 24px 0 !important',
            marginTop: '0 !important',
            color: theme.palette.text.primary,
          }),
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: ({ theme }) => ({
            padding: '8px 16px 16px !important',
            gap: '8px',
            background: `${theme.palette.background.paper} !important`,
            borderTop: '0 !important',
            '& .MuiButton-root': {
              minWidth: '64px !important',
              minHeight: '36px !important',
              padding: '6px 8px !important',
              border: '0 !important',
              borderRadius: '4px !important',
              background: 'transparent !important',
              boxShadow: 'none !important',
              color: `${theme.palette.primary.main} !important`,
              fontSize: '0.875rem !important',
              fontWeight: '500 !important',
              textTransform: 'uppercase !important',
              transform: 'none !important',
              transition: 'background-color 120ms ease',
              '&:hover': {
                background: 'rgba(77, 167, 219, 0.08) !important',
                boxShadow: 'none !important',
                transform: 'none !important',
              },
              '&.Mui-disabled': {
                background: 'transparent !important',
                color: `${theme.palette.action.disabled} !important`,
              },
            },
          }),
        },
      },
    },
  });

  // Scale heading/typography variants down on smaller screens for readability on
  // tablets and phones (desktop sizes are unchanged).
  return responsiveFontSizes(baseTheme);
};

// Default export keeps the original light theme for any direct importers.
const theme = getTheme('light');

export default theme;
